import { Sidebar } from "src/components/Sidebar/Sidebar"
import { ProjectImage } from "src/components/ProjectImage/ProjectImage"
import "./styles.scss"

const MainContent = () => {
  return (
    <div className="main-content p-4">
      <div className="header mb-4">
        <h1 className="title">Pacer Scout Tool</h1>
        <h2 className="subtitle">Full Stack Development of Pacer Scout</h2>
      </div>

      <div className="description mb-5">
        <p>
          At Pacer, I have worn many caps and lead and assisted a number of strategic projects. One of our main goals when starting out was to build a auction agregator tool, where dealerships could access used car listings from the largest auction sites in one place. On top of that, we wanted to provide a layer of analytics and research that would enhance their inventory acquisition process. 
          
        </p>
      </div>

      <div className="project-screenshot">

      <ProjectImage 
        src="/assets/Screenshots/scoutHomePage.png" 
        alt="Pacer Scout Homepage"
        description="Scout homepage showing the auction listings dashboard"
       
      />
      
      </div>
      <div className="description mb-5">
        <p>
        As the lead developer on Scout, I worked on the live auction agregation pipelines, backend APIs to access the stored live auctions, and developed much of the front end. In additional, I lead development on a Pre-Purchase-Inspection (PPI) to research individual car markets, risk factors, and provide a retail value estimate for live auctions on our site.
        </p>
      </div>

      <div className="description things-i-worked-on mb-5">
        <h4>Things I Worked On</h4>
        <p>Whilst working on Scout, I worked on some major features such as:</p>
        <ul>
          <li><a href="#ppi-tool" className="work-link">Pre-Purchase-Inspection Agent</a></li>
          <li><a href="#data-aggregation" className="work-link">Data aggregation</a></li>
          <li><a href="#backend" className="work-link">Advanced filtering</a></li>
          <li><a href="#frontend" className="work-link">Frontend</a></li>
          
        </ul>
      </div>

      <div id="data-aggregation" className="description mb-5">
        <h4>
          Data Aggregation and Pipelines
        </h4>
        <p> Reliable data aggregation was paramount to the project success. We used Selenium to scrape live auction data as well as some auction history data. I also integrated with the eBay Motors api to pull information from them.</p>
        <p>
        Once the data was collected, It was run through a pipeline to enrich the data using the free <a href="https://vpic.nhtsa.dot.gov/api/" target="_blank" rel="noopener noreferrer">NHTSA VIN Vehicle Lookup</a> API, and standardized in JSON format for storage in a Firestore database.
        </p>
        <ul>
          <li>Network of Selenium based python scrappers</li>
          <li>Google cloud run data pipelines</li>
          <li>Firestore noSQL database</li>
          <li>Ebay api integration</li>
        </ul>
        <p>In addition I experimented with building an AI based scraping tool using vertex AI structured outputs and the Crawl4AI python package. I found this combination to be quite powerful in reliably scraping any html structure, but in the end was not needed due to the limited number of auction sites we chose to scrape.  </p>
        <p>In the future, I hope to use this methodology along with AI based <a href="https://www.reddit.com/r/LocalLLaMA/comments/18iboyt/how_is_perplexity_doing_the_internet_search/?rdt=56467" target="_blank" rel="noopener noreferrer">webcrawling</a> to find and categorize live web data in real time.</p>
      </div>
      
      <div id="ppi-tool" className="description mb-5">
        <h4>Pre-Purchase-Inspection (PPI) Tool</h4>
        <p>
          One of the coolest features of Scout is the PPI tool. This tool allows dealerships to run a virtual PPI on any car, and get a detailed report on the car's condition, common issues with the model year, and a retail value estimate.
        </p>
        <p>
          I initially developed the MVP of the tool (shown in screenshots below) and later worked with a team of AI Engineering Interns to flush out features, such as image analysis and <a href="https://docs.python.org/3/library/threading.html" target="_blank" rel="noopener noreferrer">parallel threading</a>. I had a lot of fun working with the team on this project, and it was great picking up AI fundamentals while sharing my expertise in application development.
        </p>
        <h5>Methodology</h5>
        <p>To build the PPI tool, we leveraged AI APIs from Google and Perplexity based on their relative strengths. We set up these models in threaded chains, to reuse model outputs while saving time wherever possible. </p>
        <ProjectImage 
        src="/assets/Screenshots/PPI1.png" 
        alt="PPI tool"
        description="Common information is filled through our API, AI identifies listing flags"
        width="90%"
      />
        <p>
          The tool initially scrapes the vehicle information, history reports, any comments, and any images. We then used structured outputs through google vertex API to identify trends and flags in the data.
        </p>
        <ProjectImage 
        src="/assets/Screenshots/PPI2.png" 
        alt="PPI tool"
        description="Sources shown, and AI identified common issues"
        width="90%"
      />
      <p>
          We then leverage the search and reasoning capabilities of Perplexity R1 to find common issues with the vehicle. These are all delivered through strucutred output to increase consistency. We then run the vehicle specific information through the same chain to have perplexity asign a % likelihood of each issue being pressent.
        </p>
        <ProjectImage 
        src="/assets/Screenshots/PPI3.png" 
        alt="PPI tool"
        description="Repair recomendations"
        width="90%"
      />
      <ProjectImage 
        src="/assets/Screenshots/PPI4.png" 
        alt="PPI tool"
        description="Retail estimate and report conclusion"
        width="90%"
      />
      <p>
          We then use reasoning capabilities to generate a repair recommendations, and again leverage the R1 search functionality to find similar listings and price the car considering them, and the extent of damage to the vehicle.
        </p>
      <h5>Technologies Used</h5>
      <ul>
        <li>Google Vertex AI</li>
        <li>Firestore</li>
        <li>Perplexity Sonar R1</li>
        <li>python multi-threading</li>
        <li>Selenium</li>
        <li>Python Flask</li>
        <li>JSON structured outputs</li>
      </ul>
      <p>In addition, we developed a vision analysis tool to search listing images for any damage not mentioned in the vehicle description</p>
      <h5>Takeaways and next steps</h5>
      <p>This project is a testamate to the power of modern AI tools, and how they can be quickly implemented to create useful and research driven tools. If we were to have more time to work on the project, I would like to implement a more robust pricing anlytics tool, which can be used along with the webcrawling to deliver more accurate vehicle price estimates.</p>
      </div>

      <div id="frontend" className="description mb-5">
        <h4>Frontend</h4>
        <p>
          I developed much of the user interface for Scout, creating an intuitive and responsive design that allowed dealerships to easily browse and analyze auction listings.
        </p>
        <ProjectImage 
        src="/assets/Screenshots/ScoutCarPage.png" 
        alt="PPI tool"
        description="Car Details Page"
        width="100%"
      />
      <ProjectImage 
        src="/assets/Screenshots/ScoutSimilarSold.png" 
        alt="PPI tool"
        description="Similar Sold Listings on details Page"
        width="100%"
      />
      <ProjectImage 
        src="/assets/Screenshots/ScoutSimilarLive.png" 
        alt="PPI tool"
        description="Similar Live Listings on details Page"
        width="100%"
      />
      </div>

      <div id="backend" className="description mb-5">
        <h4>Backend</h4>
        <p>
          I built robust APIs to serve auction data from our database to the frontend using python flask. I also worked on the the filtering system, using caching and removal based search algorithms to speed up performance. 
        </p>
        <p>
          I also developed an algorithms that show related live listings and sold listings for a chosen vehicle listing.
        </p>
      </div>

      <div id="conclusion" className="description mb-5">
        <h4>Conclusion</h4>
        <p>
          As one of the the first full-stack projects I have worked on and lead, Scout was a great learning experience for me. There were inherit challenges with the project, such as the need for reliable data aggregation, and the need for a robust AI tool to analyze the data. I was able to overcome these challenges and deliver a product that was well received by our customers. I am proud of the work I did on this project and I am excited to continue working on similar projects in the future.
        </p>
      </div>

    </div>
  )
}

interface ProjectLayoutProps {
    sidebar: React.ReactNode;
    children: React.ReactNode;
  }

// You could create a reusable layout component like this:
const ProjectLayout: React.FC<ProjectLayoutProps> = ({ sidebar, children }) => (
  <div className="container-fluid p-0">
    <div className="row g-0">
      <div className="col-md-3">{sidebar}</div>
      <div className="col-md-9">{children}</div>
    </div>
  </div>
)

export default function PacerScoutProject() {
    const sidebarProps = {
      toolUrl: "https://scout.pacer.co",
      responsibilities: [
        "React",
        "TypeScript",
        "Ant Design",
        "Python Flask",
        "Selenium",
        "Cloud Run",
        "Firestore",
        "Google Vertex AI",
        "Perplexity Sonar R1"
      ]
    }
  
    return (
      <ProjectLayout sidebar={<Sidebar {...sidebarProps} />}>
        <MainContent />
      </ProjectLayout>
    )
}
